/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useU2000GridStateHandlers } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU2000SearchStateHandlers } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import type { U2000GridStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000SearchStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import { useU2000LUStateHandlers } from "../u2000Core/reducer/formReducer/U2000LUHandlers";
import type { U2000LUState } from "../u2000Core/reducer/formReducer/U2000LUState";
import type { U3141ChildLotChain, U3141InitLU, U3141MasterLotChain } from "./U3141Service";

// Definition of the U3141Context type
export interface U3141ContextType {
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3141MasterLotChain>;
  gridMasterChildLotChainStateHandlers: U2000GridStateHandlersReturn<U3141ChildLotChain>;
  /** Données initLU complètes (types d’opération, etc.). */
  unitInitLU: U3141InitLU;
}

//  Props for the U3141ContextProvider component
interface U3141ContextProviderProps {
  initLU: U3141InitLU;
  children: ReactNode;
}

// Creation of the U3141Context
const U3141Context = React.createContext<U3141ContextType | undefined>(undefined);

// U3141ContextProvider component to provide context values
export const U3141ContextProvider: React.FC<U3141ContextProviderProps> = ({ initLU, children }) => {
  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as U3141InitLU & U2000LUState });
  const U2000SearchStateHandlers = useU2000SearchStateHandlers({ luName: "u3141", launchSearch: initLU.launchSearch, hideSearch: initLU.hideSearch });
  const U2000GridStateHandlers = useU2000GridStateHandlers<U3141MasterLotChain>({ nbRecordPerPage: initLU.nbRecordPerPage ?? 50 });
  const U3141GridMasterChildLotChainStateHandlers = useU2000GridStateHandlers<U3141ChildLotChain>();

  const contextValue = useMemo<U3141ContextType>(
    () => ({
      luStateHandlers: U2000LUStateHandlers,
      searchStateHandlers: U2000SearchStateHandlers,
      gridStateHandlers: U2000GridStateHandlers,
      gridMasterChildLotChainStateHandlers: U3141GridMasterChildLotChainStateHandlers,
      unitInitLU: initLU,
    }),
    [U2000GridStateHandlers, U2000SearchStateHandlers, U2000LUStateHandlers, U3141GridMasterChildLotChainStateHandlers, initLU]
  );

  return <U3141Context.Provider value={contextValue}>{children}</U3141Context.Provider>;
};

// Custom hook to use the U3141Context
export function useU3141Context(): U3141ContextType {
  const context = useContext(U3141Context);
  if (!context) {
    throw new Error("useU3141Context must be used within a U3141ContextProvider");
  }
  return context;
}
